import {ContractApi} from "~/gen/openapi/sblService";
import {useAxiosClient} from "~/use/axios/client";
import {keepPreviousData, useMutation, useQuery} from "@tanstack/vue-query";
import {MS_HOUR, MS_MINUTE} from "~/constants/time";
import {Contract, FullContract} from "~/models/contract";
import {cleanDraftContract, isDraft} from "~/helpers/contracts";
import padStart from "lodash/padStart";
import {useDraftsDb} from "~/use/indexedDb/draftsDb";
import {notify} from "@gm/components";
import {isTempContract} from "~/helpers/contractHelper";
import {useProjectStore} from "~/use/project/store";
import {queryClient} from "~/plugins/vue-query";
import {useEntrepreneurService} from "~/services/entrepreneurService";

export const useContractService = () => {
    const { axiosClient } = useAxiosClient()

    const contractApi = new ContractApi(undefined, '', axiosClient.value)
    const { draftsDb } = useDraftsDb()
    const { getEntrepreneurNameById } = useEntrepreneurService()
    const { updateContractDraft } = useContractStore()

    const updateContractInContractList = (contract: FullContract) => {
        queryClient.setQueryData(['contractGetContractList'], (oldData: any) => {
            if (oldData) {
                const index = oldData.findIndex((item: Contract) => item.Id === contract.Id)
                const newData: Contract[] = [...oldData]
                let contractListItem: Contract

                const getUpdatedObject = (newContractData: FullContract, old?: Contract): Contract => {
                    const entrepreneurTeamName = !!contract.AssortmentInformation?.EntrepreneurId ? getEntrepreneurNameById(contract.AssortmentInformation?.EntrepreneurId) : ''
                    //@ts-ignore
                    return {
                        ...(old ?? {}),
                        ...newContractData,
                        OperationCostType: newContractData.AgreementDetails?.OperationCostType,
                        ForestOwnerName: old?.ForestOwnerName,
                        EntrepreneurTeamId: newContractData.AssortmentInformation?.EntrepreneurId || '',
                        EntrepreneurTeamName: entrepreneurTeamName,
                        Volume: newContractData.AssortmentInformation?.SumVolume,
                        SignedAfterOperationFelling: newContractData.EnvironmentReport?.SignedAfterOperationFelling,
                        SignedAfterOperationLogLift: newContractData.EnvironmentReport?.SignedAfterOperationLoglift,
                        SignedBeforeOperationFelling: newContractData.EnvironmentReport?.SignedBeforeOperationFelling,
                        SignedBeforeOperationLogLift: newContractData.EnvironmentReport?.SignedBeforeOperationLoglift,
                    }
                }

                if (index > -1) {
                    contractListItem = getUpdatedObject(contract, oldData[index])
                    newData.splice(index, 1, contractListItem)
                } else {
                    // add it to the contract list
                    contractListItem = getUpdatedObject(contract)
                    newData.push(contractListItem as Contract)
                }
                return newData
            }
        })
    }

    const updateContractInCache = (contract: FullContract) => {
        let updatedContract: FullContract = {}
        queryClient.setQueryData(['contractGetContract', contract.Id], (oldData: FullContract) => {
            updatedContract = {
                ...oldData,
                ...contract
            }
            return updatedContract
        })
        updateContractInContractList(updatedContract)
    }

    /**
     *
     * @param contractId
     * @param useSimpleContractAsPlaceholder - use the cached simple contract as placeholder data if the full contract is not already cached.
     * Note that you might need to use isPlaceholderData for loading indicators etc. if you set this to true.
     * https://tanstack.com/query/latest/docs/framework/vue/guides/placeholder-query-data
     */
    const contractQuery = (contractId?: string, useSimpleContractAsPlaceholder = false) => useQuery({
        queryKey: ['contractGetContract', contractId],
        queryFn: async () => {
            try {
                if (contractId?.includes('temp_')) {
                    return await draftsDb.getById(contractId) || {}
                }
                const contractData = (await contractApi.contractGetContract(contractId!))?.data
                if (contractData) {
                    updateContractInContractList(contractData as FullContract)
                }
                return contractData as FullContract
            } catch (error) {
                notify('Det skjedde en feil ved henting av kontrakten. Vennligst prøv igjen senere.', { type: 'error' })
                console.error(error)
                return Promise.reject(error)
            }
        },
        staleTime: MS_MINUTE,
        enabled: computed(() => !!contractId),
        refetchOnWindowFocus: false,
        placeholderData: () => {
            if (useSimpleContractAsPlaceholder) {
                const contractList = queryClient.getQueryData(['contractGetContractList']) as Contract[]
                return contractList?.find((c: Contract) => c.Id === contractId) as unknown as FullContract
            }
        },
        networkMode: 'offlineFirst',
        gcTime: MS_HOUR,
        retry: false
    })

    const getContractListQueryOptions = {
        queryKey: ['contractGetContractList'],
        queryFn: async () => {
            try {
                return (await contractApi.contractGetContractList())?.data || []
            } catch (error) {
                console.error(error)
                const previousData = queryClient.getQueryData(['contractGetContractList'])
                if (previousData) {
                    return previousData
                }

                return []
            }
        },
        staleTime: 30 * MS_MINUTE,
        refetchOnWindowFocus: false,
        placeholderData: keepPreviousData,
        networkMode: 'offlineFirst',
        gcTime: Infinity,
    }

    const contractListQuery = () => useQuery({
        queryKey: getContractListQueryOptions.queryKey,
        queryFn: getContractListQueryOptions.queryFn,
        staleTime: getContractListQueryOptions.staleTime,
        refetchOnWindowFocus: getContractListQueryOptions.refetchOnWindowFocus,
        placeholderData: getContractListQueryOptions.placeholderData,
        networkMode: 'offlineFirst',
        gcTime: getContractListQueryOptions.gcTime,
    })

    const contractProjectQuery = (contractId?: string) => useQuery({
        queryKey: ['contractGetContractProject', contractId],
        queryFn: async () => {
            try {
                const projectStore = useProjectStore()
                projectStore.resetProjectOrders()
                projectStore.resetFinancialReport()

                if (!contractId || (contractId && isTempContract(contractId))) {
                    return {}
                }
                return (await contractApi.contractGetContractProject(contractId)).data
            } catch (error) {
                console.error(error)
                return Promise.reject(error)
            }
        },
        staleTime: 5 * MS_MINUTE,
        enabled: computed(() => !!contractId),
        refetchOnWindowFocus: false,
        placeholderData: keepPreviousData,
        networkMode: 'offlineFirst',
        gcTime: MS_HOUR,
        retry: false
    })

    const contractPdfQuery = (contractId?: string) => useQuery({
        queryKey: ['contractGetContractPdf', contractId],
        queryFn: async () => {
            try {
                if (!contractId) {
                    return Promise.reject('Missing contract ID')
                }
                return (await contractApi.contractGetContractPdf(contractId, {responseType: 'blob'}))?.data
            } catch (error) {
                console.error(error)
                return Promise.reject(error)
            }
        },
        staleTime: MS_MINUTE,
        enabled: computed(() => !!contractId),
        refetchOnWindowFocus: false,
        placeholderData: keepPreviousData,
        networkMode: 'offlineFirst',
    })

    const saveContractMutation = () => useMutation({
        mutationKey: ['saveContract'],
        mutationFn: async ({contract, optimisticUpdate = false}: {contract: FullContract, optimisticUpdate?: boolean}) => {
            const draft = isDraft(contract)
            const cleanedContract = draft ? cleanDraftContract(contract) : contract

            if (draft) {
                return contractApi.contractCreateContract(cleanedContract)
            } else {
                if (optimisticUpdate) {
                    updateContractInCache(cleanedContract)
                }
                return contractApi.contractUpdateContract(cleanedContract.Id!, cleanedContract)
            }
        },
        async onSuccess(response, variables) {
            const responseData = response.data as unknown as FullContract

            if (isDraft(variables.contract)) {
                // Handle local draft
                queryClient.invalidateQueries({queryKey: ['contractGetContract', variables.contract.Id]}).then(() =>
                    // Need to refetch instead of setQueryData when POST since the response is missing a lot of data.
                    queryClient.refetchQueries({queryKey: ['contractGetContract', responseData.Id]})
                )
            } else {
                if (!variables.optimisticUpdate) {
                    queryClient.setQueryData(['contractGetContract', responseData.Id], () => responseData)
                }
            }
            if (!variables.optimisticUpdate) {
                updateContractDraft(responseData)
            }
            updateContractInContractList(responseData as FullContract)
            notify('Kontrakt lagret', {
                icon: 'check',
                type: 'success'
            })
        },
        async onError(error, variables) {
            if (variables.optimisticUpdate) {
                // reset changes
                queryClient?.invalidateQueries({queryKey: ['contractGetContract', variables.contract.Id]})
                queryClient?.refetchQueries({queryKey: ['contractGetContractList']})
            }
            notify('Det skjedde en feil ved lagring av kontrakt. Ta kontakt med IT Support', {
                icon: 'exclamation',
                type: 'error'
            })
            console.error(error)
        },
    })

    const activateContractMutation = () => useMutation({
        mutationFn: async (contractId: string) => {
            return contractApi.contractForestOperatorContractActivation(contractId)
        },
        onSettled: (_data, _error, variables) => {
            queryClient?.refetchQueries({queryKey: ['contractGetContract', variables]})
        }
    })

    const updateContractSorting = (contractId: string, sorting: number) => {
        queryClient.setQueryData(['contractGetContractList'], (oldData: Contract[]) => {
            if (oldData) {
                const index = oldData.findIndex((item: Contract) => item.Id === contractId)
                if (index > -1) {
                    const newData = [...oldData]
                    let updatedContract = { ...newData[index], Sorting: sorting }

                    newData.splice(index, 1, updatedContract)
                    return newData;
                }
            }
            return []
        })
    }

    const saveContractSortingMutation = () => useMutation({
        mutationFn: async ({contractId, sorting}: {contractId: string, sorting: number}) => {
            return contractApi.contractSetContractSorting(contractId, sorting)
        },
        onSuccess(_, variables) {
            updateContractSorting(variables.contractId, variables.sorting)
        }
    })

    const updateContractPeriodMutation = () => useMutation({
        mutationFn: async (c: FullContract) => {
            return contractApi.contractUpdateContractPeriodAction(c.Id!, `${c.Year}${padStart(''+c.LoggingMonth, 2, '0')}`)
        },
        onSuccess(_, c) {
           queryClient.setQueryData(['contractGetContract', c.Id], () => c)
        }
    })


    const getContractsByProperty = (propertyId: string, contractList: Ref<Contract[] | undefined>) => {
        return contractList.value?.filter((contract: Contract) => contract.PropertyId === propertyId)
    }

    return {
        updateContractInContractList,
        contractQuery,
        contractListQuery,
        contractProjectQuery,
        saveContractMutation,
        saveContractSortingMutation,
        updateContractPeriodMutation,
        getContractsByProperty,
        activateContractMutation,
        getContractListQueryOptions,
        contractPdfQuery,
    }
}
